import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Skeleton,
  Fade,
  alpha,
  Avatar,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Tab,
  Tabs,
  Grid,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  CloudDownload as ImportIcon,
  Store as StoreIcon,
  Speed as SpeedIcon,
  PlayArrow as RunningIcon,
} from '@mui/icons-material';
import { supabase } from '../../lib/supabase';
import { adminApi } from '../../lib/adminApi';

// ============================================================================
// TYPES
// ============================================================================

interface SyncLog {
  id: number;
  shop_id: string;
  shop_name: string | null;
  started_at: string | null;
  finished_at: string | null;
  status: string | null;
  products_created: number | null;
  products_updated: number | null;
  products_skipped: number | null;
  products_failed: number | null;
  error_message: string | null;
  details: any;
}

interface FeedImportLog {
  id: number;
  supplier_id: number | null;
  supplier_name: string;
  started_at: string | null;
  completed_at: string | null;
  status: string | null;
  products_created: number | null;
  products_updated: number | null;
  products_errors: number | null;
  error_message: string | null;
  duration_seconds: number | null;
}

// ============================================================================
// FETCH FUNCTIONS
// ============================================================================

const fetchSyncLogs = async (): Promise<SyncLog[]> => {
  return adminApi.syncLogs.list(10) as Promise<SyncLog[]>;
};

const fetchFeedImportLogs = async (): Promise<FeedImportLog[]> => {
  return adminApi.feedImportLogs.list(10) as Promise<FeedImportLog[]>;
};

// ============================================================================
// STAT CARD COMPONENT
// ============================================================================

const StatCard = ({
  title,
  value,
  icon: Icon,
  color,
  subtitle,
}: {
  title: string;
  value: number | string;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
}) => (
  <Card
    sx={{
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar
          sx={{
            bgcolor: alpha(color, 0.1),
            color: color,
            width: 44,
            height: 44,
          }}
        >
          <Icon />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.disabled">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// ============================================================================
// SYNC LOG ROW COMPONENT
// ============================================================================

const SyncLogRow = ({ log }: { log: SyncLog }) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'completed_with_errors':
        return <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
      case 'failed':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      case 'running':
        return <RunningIcon sx={{ color: 'info.main', fontSize: 20 }} />;
      default:
        return <ScheduleIcon sx={{ color: 'grey.500', fontSize: 20 }} />;
    }
  };

  const getStatusColor = (status: string | null): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'completed_with_errors':
        return 'warning';
      case 'failed':
        return 'error';
      case 'running':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (ts: string | null) => {
    if (!ts) return '-';
    const date = new Date(ts);
    return date.toLocaleString('bs-BA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getDuration = (start: string | null, end: string | null) => {
    if (!start || !end) return '-';
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded(!expanded)}
        sx={{
          cursor: 'pointer',
          bgcolor: log.status === 'failed' ? alpha('#ef4444', 0.05) : 'inherit',
          '&:hover': {
            bgcolor: log.status === 'failed' ? alpha('#ef4444', 0.1) : alpha('#3b82f6', 0.05),
          },
        }}
      >
        <TableCell sx={{ width: 40 }}>
          {getStatusIcon(log.status)}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {formatTimestamp(log.started_at)}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            icon={<StoreIcon sx={{ fontSize: 14 }} />}
            label={log.shop_name || log.shop_id}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        </TableCell>
        <TableCell>
          <Chip
            label={log.status || 'unknown'}
            size="small"
            color={getStatusColor(log.status)}
            sx={{ textTransform: 'capitalize', fontWeight: 500 }}
          />
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={`+${log.products_created || 0}`} color="success" variant="outlined" />
            <Chip size="small" label={`~${log.products_updated || 0}`} color="info" variant="outlined" />
            {(log.products_failed || 0) > 0 && (
              <Chip size="small" label={`!${log.products_failed}`} color="error" variant="outlined" />
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {getDuration(log.started_at, log.finished_at)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded ? 1 : 0, borderColor: 'divider' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Statistike
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>Kreirano:</strong> {log.products_created || 0}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Ažurirano:</strong> {log.products_updated || 0}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Preskočeno:</strong> {log.products_skipped || 0}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Greške:</strong> {log.products_failed || 0}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Detalji / Log poruke
                  </Typography>
                  {log.error_message && (
                    <Paper sx={{ p: 1.5, mb: 1, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
                      <Typography variant="body2" color="error.main">
                        {log.error_message}
                      </Typography>
                    </Paper>
                  )}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      maxHeight: 200,
                      overflow: 'auto',
                      bgcolor: 'grey.50',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                    }}
                  >
                    {log.details?.logs && log.details.logs.length > 0 ? (
                      log.details.logs.map((entry: any, idx: number) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          component="div"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            color: entry.level === 'error'
                              ? 'error.main'
                              : entry.level === 'success'
                              ? 'success.main'
                              : entry.level === 'warning'
                              ? 'warning.main'
                              : 'text.primary',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          [{entry.timestamp?.split('T')[1]?.split('.')[0] || ''}] {entry.message}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        Nema detaljnih log poruka
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ============================================================================
// FEED IMPORT LOG ROW COMPONENT
// ============================================================================

const FeedImportLogRow = ({ log }: { log: FeedImportLog }) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'completed':
      case 'success':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'failed':
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      case 'running':
        return <RunningIcon sx={{ color: 'info.main', fontSize: 20 }} />;
      default:
        return <ScheduleIcon sx={{ color: 'grey.500', fontSize: 20 }} />;
    }
  };

  const getStatusColor = (status: string | null): 'success' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'completed':
      case 'success':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'running':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (ts: string | null) => {
    if (!ts) return '-';
    const date = new Date(ts);
    return date.toLocaleString('bs-BA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${(seconds / 60).toFixed(1)}min`;
  };

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded(!expanded)}
        sx={{
          cursor: 'pointer',
          bgcolor: log.status === 'failed' ? alpha('#ef4444', 0.05) : 'inherit',
          '&:hover': {
            bgcolor: log.status === 'failed' ? alpha('#ef4444', 0.1) : alpha('#3b82f6', 0.05),
          },
        }}
      >
        <TableCell sx={{ width: 40 }}>
          {getStatusIcon(log.status)}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {formatTimestamp(log.started_at)}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            icon={<ImportIcon sx={{ fontSize: 14 }} />}
            label={log.supplier_name}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: '0.75rem' }}
          />
        </TableCell>
        <TableCell>
          <Chip
            label={log.status || 'unknown'}
            size="small"
            color={getStatusColor(log.status)}
            sx={{ textTransform: 'capitalize', fontWeight: 500 }}
          />
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={`+${log.products_created || 0}`} color="success" variant="outlined" />
            <Chip size="small" label={`~${log.products_updated || 0}`} color="info" variant="outlined" />
            {(log.products_errors || 0) > 0 && (
              <Chip size="small" label={`!${log.products_errors}`} color="error" variant="outlined" />
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDuration(log.duration_seconds)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded ? 1 : 0, borderColor: 'divider' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Detalji
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>Supplier ID:</strong> {log.supplier_id || '-'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Početak:</strong> {formatTimestamp(log.started_at)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Kraj:</strong> {formatTimestamp(log.completed_at)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Trajanje:</strong> {formatDuration(log.duration_seconds)}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  {log.error_message && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Greška
                      </Typography>
                      <Paper sx={{ p: 1.5, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
                        <Typography variant="body2" color="error.main" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {log.error_message}
                        </Typography>
                      </Paper>
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Logs() {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');

  // Fetch sync logs
  const {
    data: syncLogs,
    isLoading: syncLogsLoading,
    refetch: refetchSyncLogs,
    isFetching: syncLogsFetching,
  } = useQuery({
    queryKey: ['syncLogs'],
    queryFn: fetchSyncLogs,
    refetchInterval: 10000,
  });

  // Fetch feed import logs
  const {
    data: feedImportLogs,
    isLoading: feedImportLogsLoading,
    refetch: refetchFeedImportLogs,
    isFetching: feedImportLogsFetching,
  } = useQuery({
    queryKey: ['feedImportLogs'],
    queryFn: fetchFeedImportLogs,
    refetchInterval: 10000,
  });

  const handleRefresh = () => {
    if (activeTab === 0) {
      refetchSyncLogs();
    } else {
      refetchFeedImportLogs();
    }
  };

  // Calculate stats for sync logs
  const syncStats = {
    total: syncLogs?.length || 0,
    completed: syncLogs?.filter(l => l.status === 'completed').length || 0,
    failed: syncLogs?.filter(l => l.status === 'failed').length || 0,
    totalCreated: syncLogs?.reduce((sum, l) => sum + (l.products_created || 0), 0) || 0,
    totalUpdated: syncLogs?.reduce((sum, l) => sum + (l.products_updated || 0), 0) || 0,
  };

  // Calculate stats for feed import logs
  const feedStats = {
    total: feedImportLogs?.length || 0,
    completed: feedImportLogs?.filter(l => l.status === 'completed' || l.status === 'success').length || 0,
    failed: feedImportLogs?.filter(l => l.status === 'failed' || l.status === 'error').length || 0,
    totalCreated: feedImportLogs?.reduce((sum, l) => sum + (l.products_created || 0), 0) || 0,
    totalUpdated: feedImportLogs?.reduce((sum, l) => sum + (l.products_updated || 0), 0) || 0,
  };

  // Get unique suppliers for filter
  const uniqueSuppliers = [...new Set(feedImportLogs?.map(l => l.supplier_name) || [])];

  // Filter feed import logs
  const filteredFeedImportLogs = selectedSupplier === 'all'
    ? feedImportLogs
    : feedImportLogs?.filter(l => l.supplier_name === selectedSupplier);

  const isLoading = activeTab === 0 ? syncLogsLoading : feedImportLogsLoading;
  const isFetching = activeTab === 0 ? syncLogsFetching : feedImportLogsFetching;

  return (
    <Box sx={{ width: '100%', p: { xs: 0, sm: 2, md: 3 } }}>
      {/* Header */}
      <Fade in={true} timeout={500}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700}>
              Logovi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pregled svih sync i import operacija
            </Typography>
          </Box>
          <Tooltip title="Osvježi">
            <IconButton
              onClick={handleRefresh}
              disabled={isFetching}
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Fade>

      {/* Progress indicator */}
      {isFetching && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Tabs */}
      <Fade in={true} timeout={600}>
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab
              icon={<SyncIcon />}
              iconPosition="start"
              label={`WooCommerce Sync (${syncStats.total})`}
            />
            <Tab
              icon={<ImportIcon />}
              iconPosition="start"
              label={`Feed Import (${feedStats.total})`}
            />
          </Tabs>
        </Card>
      </Fade>

      {/* Stats */}
      <Fade in={true} timeout={700}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            {activeTab === 0 ? (
              <>
                <Grid item xs={6} sm={4} md={2.4}>
                  <StatCard title="Ukupno" value={syncStats.total} icon={SyncIcon} color="#3b82f6" />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <StatCard title="Uspješno" value={syncStats.completed} icon={CheckCircleIcon} color="#10b981" />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <StatCard title="Neuspješno" value={syncStats.failed} icon={ErrorIcon} color="#ef4444" />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <StatCard title="Kreirano" value={syncStats.totalCreated} icon={AddIcon} color="#8b5cf6" />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <StatCard title="Ažurirano" value={syncStats.totalUpdated} icon={EditIcon} color="#f59e0b" />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={6} sm={4} md={2.4}>
                  <StatCard title="Ukupno" value={feedStats.total} icon={ImportIcon} color="#3b82f6" />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <StatCard title="Uspješno" value={feedStats.completed} icon={CheckCircleIcon} color="#10b981" />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <StatCard title="Neuspješno" value={feedStats.failed} icon={ErrorIcon} color="#ef4444" />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <StatCard title="Kreirano" value={feedStats.totalCreated} icon={AddIcon} color="#8b5cf6" />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <StatCard title="Ažurirano" value={feedStats.totalUpdated} icon={EditIcon} color="#f59e0b" />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Fade>

      {/* Filter for Feed Import */}
      {activeTab === 1 && (
        <Fade in={true} timeout={750}>
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Dobavljač</InputLabel>
                <Select
                  value={selectedSupplier}
                  label="Dobavljač"
                  onChange={(e: SelectChangeEvent) => setSelectedSupplier(e.target.value)}
                >
                  <MenuItem value="all">Svi dobavljači</MenuItem>
                  {uniqueSuppliers.map((supplier) => (
                    <MenuItem key={supplier} value={supplier}>
                      {supplier}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Table */}
      <Fade in={true} timeout={800}>
        <Card sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ width: 40 }}></TableCell>
                  <TableCell>Vrijeme</TableCell>
                  <TableCell>{activeTab === 0 ? 'Shop' : 'Dobavljač'}</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rezultat</TableCell>
                  <TableCell>Trajanje</TableCell>
                  <TableCell align="right">Detalji</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  [...Array(10)].map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell colSpan={7}>
                        <Skeleton variant="rectangular" height={40} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : activeTab === 0 ? (
                  syncLogs && syncLogs.length > 0 ? (
                    syncLogs.map((log) => <SyncLogRow key={log.id} log={log} />)
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Stack alignItems="center" spacing={2}>
                          <SyncIcon sx={{ fontSize: 48, color: 'grey.300' }} />
                          <Typography color="text.secondary">
                            Nema sync logova
                          </Typography>
                          <Typography variant="body2" color="text.disabled">
                            Logovi će se pojaviti kada pokrenete WooCommerce sync
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                ) : (
                  filteredFeedImportLogs && filteredFeedImportLogs.length > 0 ? (
                    filteredFeedImportLogs.map((log) => <FeedImportLogRow key={log.id} log={log} />)
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Stack alignItems="center" spacing={2}>
                          <ImportIcon sx={{ fontSize: 48, color: 'grey.300' }} />
                          <Typography color="text.secondary">
                            Nema feed import logova
                          </Typography>
                          <Typography variant="body2" color="text.disabled">
                            Logovi će se pojaviti kada pokrenete import feeda
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Fade>
    </Box>
  );
}
